import { HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IDataset } from '../models/dataset';

/** Injectable service that provides access to the LATIS API */
@Injectable({ providedIn: 'root' })
export class Latis {
    // the URL base for most latis operations
    latisBase = '';
    // the URL base for full outer join operations (for downloading a csv of multiple datasets)
    latisJoinBase = 'join/';

    timeFormatters = {
        /**
         * Creates a query parameter to be used when downloading CSV data,
         * instructing latis to format the date as seconds since a certain time.
         */
        secondsSinceT0: function( dateT0: Date ) {
            return '&convert(time,seconds since ' + dateT0.toISOString() + ')';
        },
        /**
         * Creats a query parameter to be used when downloading CSV data,
         * instructing latis to format the date as either an ISO-like string or a DOY string.
         */
        simple: function( timeFormat: string ) {
            let formatString;
            switch ( timeFormat ) {
            case 'YYYY-DDDD':
                formatString = 'yyyy-DDD\'T\'HH:mm:ss.SSS';
                break;
            case 'YYYY-MM-DD':
                formatString = 'yyyy-MM-dd\'T\'HH:mm:ss.SSS';
                break;
            default:
                console.error( 'Programmer error: timeFormat not recognized: ' + timeFormat );
                formatString = '';
            }

            return '&format_time(' + formatString + ')';
        }
    };

    /**
     * Copied from here: http://stackoverflow.com/questions/470832/getting-an-absolute-url-from-a-relative-one-ie6-issue#answer-472729
     */
    escapeHTML( s: string ) {
        // Comment from Ransom:
        // > Very clever, but it took me a few moments to realize you were using split/join
        // > as a global string replace. Note that using replace() with a global flag is
        // > much faster than split/join (at least in Chrome), so consider replace() if
        // > this function is ever churning through large amounts of strings
        //
        // http://stash.lasp.colorado.edu/projects/WEBAPPS/repos/lisird3/pull-requests/48/overview
        return s.split('&').join('&amp;').split('<').join('&lt;').split('"').join('&quot;');
    }

    /** returns a http request for a latis resource */
    get( url: string ) {
        return new HttpRequest('GET', url, { responseType: 'text', reportProgress: true } );
    }

    /** set the latis base url */
    setBase( newBase: string ) {
        this.latisBase = newBase;
        if ( !this.latisBase.endsWith('/') ) {
            this.latisBase += '/';
        }
    }

    /** set the latis join base url */
    setJoinBase( newJoinBase: string ) {
        this.latisJoinBase = newJoinBase;
        if ( !this.latisJoinBase.endsWith('/') ) {
            this.latisJoinBase += '/';
        }
    }

    /** performs formatting on the set latis base to ensure it is a well formed url */
    getFullyQualifiedLatisBase() {
        if ( this.latisBase.indexOf('://') === -1 ) {
            // the full URL will the the app's URL root plus the latisBase
            // construct an <a> element with a relative link, and let it resolve the relative URL to a full one
            const a = document.createElement('a');
            a.href = this.latisBase;
            return a.href;
        } else {
            // latisBase is already a fully qualified URL
            return this.latisBase;
        }
    }

    /**
     * Modified from here: http://stackoverflow.com/questions/470832/getting-an-absolute-url-from-a-relative-one-ie6-issue#answer-472729
     */
    qualifyURL = function( url: string ) {
        const el = document.createElement('div');
        el.innerHTML = '<a href="' + this.escapeHTML( this.latisBase + url ) + '"></a>';
        return el.firstChild['href'];
    };

    /** request a dataset download from latis */
    getDatasetDownload( identifier: string, parameters: string, startTime: number, endTime: number ) {
        const url = this.latisBase + identifier + '.' + parameters;
        if ( typeof startTime !== 'undefined' && typeof endTime !== 'undefined') {
            window.open( url + '&time>=' + startTime + '&time<=' + endTime );
        } else {
            window.open( url );
        }
    }

    /** some datasets have pre-binned datasets for plotting, but we want to convert to full resolution for downloads */
    convertToFullResURL( datasetURL: string ) {
        // Helper function for downloadCSV
        // All "Auto" downloads should contain full resolution data (WEBTCAD-1174)
        // Script should also preserve text all after TelemetryItem (WEBTCAD-1177)

        if (( datasetURL.indexOf('Auto') !== -1 ) && ( datasetURL.indexOf('Analog') !== -1 )) {
            datasetURL = datasetURL.replace( /.+?(?=TelemetryItem)/, 'Analog');
        } else if (( datasetURL.indexOf('Auto') !== -1 ) && (datasetURL.indexOf('Discrete') !== -1 )) {
            datasetURL = datasetURL.replace( /.+?(?=TelemetryItem)/, 'Discrete');
        }

        return datasetURL;
    }

    /**
     * Downloads CSVs for one or more datasets
     */
    downloadCSV( datasets: IDataset[], timeFormatQueryParam: string ) {
        timeFormatQueryParam = timeFormatQueryParam || '';
        const datasetURLs = datasets.map( dataset => dataset.fullURL );
        if ( datasets.length === 1 ) {
            datasetURLs[0] = this.convertToFullResURL( datasetURLs[0] );
            // if there's only one CSV to download, open a new window and make a GET request to latis
            window.open( datasetURLs[0].replace('.jsond', '.csv') );
        } else {
            // latis can merge multiple datasets together into one csv
            // in order to do this, we need to POST to it a list of the datasets to get
            //
            // Normally, a file download can only be triggered by setting the URL of a browser window
            // This would potentially send a very long GET request to the server--our list of datasets and parameters to download could
            // potentially be rather long GET request lengths max out at much lower request lengths than POST requests do. So we want
            // to trigger a file download via POST request. The only way to do this is to submit a form (POST method) with the download
            // query information contained in the form

            for ( let i = 0; i < datasetURLs.length; i++ ) {
                datasetURLs[i] = this.convertToFullResURL( datasetURLs[i] );
                // construct the URLs, replacing 'jsond' with 'json', and appending the time format.
                // The format should always be ISO-8601 (WEBTCAD-1110)
                datasetURLs[i] = datasetURLs[i].replace( '.jsond', '.json' );
            }

            // rather than appending the time formatting string to each dataset URL,
            // we need to append it to the service URL, as a GET parameter
            const input = document.createElement( 'input' );
            input.name = 'urls';
            input.hidden = true;
            input.value = datasetURLs.join(';'); // the latis join service requires that urls are semicolon-delimited
            const form = document.createElement( 'form' );
            form.method = 'post';
            form.action = this.latisJoinBase + '?' + timeFormatQueryParam;
            form.append( input );
            document.body.append( form ); // not sure if the form even needs to be added to the DOM
            form.submit();
            form.remove();
        }
    }
}
